// ═══════════════════════════════════════════════════════════════════════════════
// ShowcaseDestinationsTab — Branding > Destinations tab
//
// Allows tenant admins to:
//   1. Toggle the destinations section on/off (show_destinations)
//   2. Choose between default platform destinations or custom destinations
//   3. Pick a layout for default or custom destinations
//   4. CRUD manage custom showcase destinations with full travel card fields
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, ChevronUp, ChevronDown, Image as ImageIcon,
  ExternalLink, Eye, EyeOff, MapPin, Tag, Calendar, DollarSign,
  LayoutGrid, GalleryHorizontalEnd, Rows3, Layers, Sparkles, List,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import { Modal, Spinner } from '@/components/shared/UIComponents';
import {
  fetchShowcaseDestinations,
  createShowcaseDestination,
  updateShowcaseDestination,
  deleteShowcaseDestination,
  reorderShowcaseDestinations,
} from '@/lib/api';
import type { ShowcaseDestination, CreateShowcaseDestinationInput } from '@/types';
import { DESTINATION_PRESETS, type DestinationPreset } from './destinationPresets';

// ── Layout options ───────────────────────────────────────────────────────────

const DEFAULT_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
  { value: 'list', label: 'List', emoji: '☰' },
] as const;

const CUSTOM_LAYOUTS = [
  { value: 'cards',    label: 'Cards',    icon: LayoutGrid,            description: 'Equal-height card grid with image, title, price & season' },
  { value: 'magazine', label: 'Magazine', icon: Layers,                description: 'Hero feature card + smaller grid below' },
  { value: 'carousel', label: 'Carousel', icon: GalleryHorizontalEnd, description: 'Horizontal scroll with rich detail cards' },
  { value: 'masonry',  label: 'Masonry',  icon: Sparkles,             description: 'Pinterest-style staggered grid, varying heights' },
  { value: 'overlay',  label: 'Overlay',  icon: ImageIcon,            description: 'Full-bleed images with text overlay, editorial feel' },
  { value: 'minimal',  label: 'Minimal',  icon: List,                 description: 'Clean list with thumbnails, text-focused layout' },
] as const;

// ── Empty form ───────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '',
  subtitle: '',
  description: '',
  image_url: '',
  price_range: '',
  best_season: '',
  tags: '',
  link_url: '',
  is_active: true,
};

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  tenantId: string;
  showDestinations: boolean;
  destinationsLayout: string;
  useCustomDestinations: boolean;
  customDestinationsLayout: string;
  onFieldChange: (field: string, value: any) => void;
}

// ═══════════════════════════════════════════════════════════════════════════════

export function ShowcaseDestinationsTab({
  tenantId,
  showDestinations,
  destinationsLayout,
  useCustomDestinations,
  customDestinationsLayout,
  onFieldChange,
}: Props) {
  // ── Destinations CRUD state ──────────────────────────────────────────
  const [destinations, setDestinations] = useState<ShowcaseDestination[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Modal state ──────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Delete confirmation ──────────────────────────────────────────────
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Load destinations ────────────────────────────────────────────────
  const loadDestinations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchShowcaseDestinations(tenantId);
      setDestinations(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load destinations');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadDestinations(); }, [loadDestinations]);

  // ── Open modal for add / edit ────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  // Open the add/edit modal pre-filled from a preset. Image URL is
  // intentionally left blank — admin supplies their own brand-fit
  // imagery. Tags collapse into the existing comma-separated string
  // input. Tenants can still edit anything before saving, or close
  // the modal without saving if the preset wasn't right.
  const openFromPreset = (preset: DestinationPreset) => {
    setEditingId(null);
    setForm({
      name: preset.name,
      subtitle: preset.subtitle,
      description: preset.description,
      image_url: '',
      price_range: preset.price_range,
      best_season: preset.best_season,
      tags: preset.tags.join(', '),
      link_url: '',
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (dest: ShowcaseDestination) => {
    setEditingId(dest.id);
    setForm({
      name: dest.name,
      subtitle: dest.subtitle || '',
      description: dest.description || '',
      image_url: dest.image_url || '',
      price_range: dest.price_range || '',
      best_season: dest.best_season || '',
      tags: (dest.tags || []).join(', '),
      link_url: dest.link_url || '',
      is_active: dest.is_active,
    });
    setModalOpen(true);
  };

  // ── Save (create / update) ───────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');

    const tagsArray = form.tags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    const payload: CreateShowcaseDestinationInput = {
      name: form.name.trim(),
      subtitle: form.subtitle.trim() || null,
      description: form.description.trim() || null,
      image_url: form.image_url.trim() || null,
      price_range: form.price_range.trim() || null,
      best_season: form.best_season.trim() || null,
      tags: tagsArray,
      link_url: form.link_url.trim() || null,
      is_active: form.is_active,
    };

    try {
      if (editingId) {
        await updateShowcaseDestination(editingId, tenantId, payload);
      } else {
        await createShowcaseDestination(tenantId, {
          ...payload,
          sort_order: destinations.length,
        });
      }
      setModalOpen(false);
      await loadDestinations();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteShowcaseDestination(deleteId, tenantId);
      setDeleteId(null);
      await loadDestinations();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  // ── Reorder ──────────────────────────────────────────────────────────
  const moveUp = async (index: number) => {
    if (index === 0) return;
    const ids = destinations.map(d => d.id);
    [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
    // Optimistic UI update
    const updated = [...destinations];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setDestinations(updated);
    await reorderShowcaseDestinations(tenantId, ids);
  };

  const moveDown = async (index: number) => {
    if (index >= destinations.length - 1) return;
    const ids = destinations.map(d => d.id);
    [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
    const updated = [...destinations];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setDestinations(updated);
    await reorderShowcaseDestinations(tenantId, ids);
  };

  // ── Toggle active/inactive inline ────────────────────────────────────
  const toggleActive = async (dest: ShowcaseDestination) => {
    try {
      await updateShowcaseDestination(dest.id, tenantId, { is_active: !dest.is_active });
      setDestinations(prev =>
        prev.map(d => d.id === dest.id ? { ...d, is_active: !d.is_active } : d)
      );
    } catch (err: any) {
      setError(err.message || 'Failed to update');
    }
  };

  // ── Form field helper ────────────────────────────────────────────────
  const setField = (key: string, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  // ═════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════

  return (
    <div className="space-y-5">

      {/* ── Section 1: Show Destinations Toggle ────────────────────────── */}
      <div className="glass-panel p-5">
        <h3 className="text-sm font-semibold text-surface-900 mb-4">Destinations Visibility</h3>
        <label className="flex items-center gap-3 cursor-pointer">
          <button
            type="button"
            role="switch"
            aria-checked={showDestinations}
            onClick={() => onFieldChange('show_destinations', !showDestinations)}
            className={cn(
              'w-11 h-6 rounded-full transition-colors relative shrink-0',
              showDestinations ? 'bg-emerald-500' : 'bg-slate-400'
            )}
          >
            <div
              className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
              style={{ transform: showDestinations ? 'translateX(20px)' : 'translateX(0)' }}
            />
          </button>
          <div>
            <span className="text-sm font-medium text-surface-700">Show Destinations Section</span>
            <p className="text-xs text-surface-500">Toggle the Popular Destinations section on the splash page</p>
          </div>
        </label>
      </div>

      {showDestinations && (
        <>
          {/* ── Section 2: Default vs Custom toggle ──────────────────── */}
          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-4">Destination Source</h3>
            <div className="flex gap-3">
              <button
                onClick={() => onFieldChange('use_custom_destinations', false)}
                className={cn(
                  'flex-1 p-3 rounded-xl border-2 text-left transition-all',
                  !useCustomDestinations
                    ? 'border-emerald-500 bg-emerald-500/5'
                    : 'border-surface-300 hover:border-surface-400'
                )}
              >
                <div className="text-sm font-semibold text-surface-900">Default Destinations</div>
                <p className="text-xs text-surface-500 mt-1">Use the platform's curated destination set (Bali, Tokyo, Paris, etc.)</p>
              </button>
              <button
                onClick={() => onFieldChange('use_custom_destinations', true)}
                className={cn(
                  'flex-1 p-3 rounded-xl border-2 text-left transition-all',
                  useCustomDestinations
                    ? 'border-emerald-500 bg-emerald-500/5'
                    : 'border-surface-300 hover:border-surface-400'
                )}
              >
                <div className="text-sm font-semibold text-surface-900">Custom Destinations</div>
                <p className="text-xs text-surface-500 mt-1">Define your own showcase destinations with rich detail cards</p>
              </button>
            </div>
          </div>

          {/* ── Section 3: Layout picker ──────────────────────────────── */}
          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-4">
              {useCustomDestinations ? 'Custom Layout Style' : 'Default Layout'}
            </h3>

            {!useCustomDestinations ? (
              /* Default layouts: grid / carousel / list */
              <div className="flex gap-2">
                {DEFAULT_LAYOUTS.map(layout => (
                  <button
                    key={layout.value}
                    onClick={() => onFieldChange('destinations_layout', layout.value)}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-center transition-all',
                      destinationsLayout === layout.value
                        ? 'border-emerald-500/50 badge-emerald'
                        : 'border-surface-300 text-surface-500 hover:border-surface-400'
                    )}
                  >
                    <span className="text-lg">{layout.emoji}</span>
                    <span className="text-xs font-medium">{layout.label}</span>
                  </button>
                ))}
              </div>
            ) : (
              /* Custom WordPress-style layouts */
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {CUSTOM_LAYOUTS.map(layout => {
                  const Icon = layout.icon;
                  return (
                    <button
                      key={layout.value}
                      onClick={() => onFieldChange('custom_destinations_layout', layout.value)}
                      className={cn(
                        'flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 text-center transition-all',
                        customDestinationsLayout === layout.value
                          ? 'border-emerald-500 badge-emerald'
                          : 'border-surface-300 text-surface-500 hover:border-surface-400 hover:text-surface-700'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs font-semibold">{layout.label}</span>
                      <span className="text-[10px] leading-tight text-surface-400">{layout.description}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Section 3.5: Preset library ──────────────────────────────
              One-tap starter cards for common destinations. Picking
              one pre-fills the add-destination modal — admin can edit
              copy / add image / save, or cancel if it's not a fit.
              Presets the tenant has already added (matched by name)
              are dimmed but still clickable in case they want a
              second copy. */}
          {useCustomDestinations && (
            <div className="glass-panel p-5">
              <div className="flex items-start gap-3 mb-4">
                <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-surface-900">Start from a preset</h3>
                  <p className="text-xs text-surface-500 mt-0.5">
                    Magazine-style copy for popular destinations. Pick one to pre-fill the form — tweak the wording, add your own image, save.
                    You can still build fully custom destinations below.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {DESTINATION_PRESETS.map((preset) => {
                  const alreadyAdded = destinations.some(
                    (d) => d.name.trim().toLowerCase() === preset.name.toLowerCase()
                  );
                  return (
                    <button
                      key={preset.key}
                      onClick={() => openFromPreset(preset)}
                      className={cn(
                        'group flex items-start gap-2 p-3 rounded-lg border text-left transition-all',
                        alreadyAdded
                          ? 'border-surface-200 bg-surface-50 hover:border-surface-300 opacity-70'
                          : 'border-surface-200 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10',
                      )}
                      title={alreadyAdded ? 'Already in your destinations — click to add another' : `Add ${preset.name}`}
                    >
                      <span className="text-xl shrink-0 leading-none">{preset.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-surface-900 truncate">{preset.name}</span>
                          {alreadyAdded && (
                            <span className="px-1 py-0.5 rounded text-[9px] font-semibold bg-surface-200 text-surface-600">added</span>
                          )}
                        </div>
                        <span className="text-[11px] text-surface-500 line-clamp-1">{preset.subtitle}</span>
                      </div>
                      <Plus className="w-3.5 h-3.5 text-surface-400 group-hover:text-emerald-600 shrink-0 mt-0.5" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Section 4: Custom Destinations CRUD ───────────────────── */}
          {useCustomDestinations && (
            <div className="glass-panel p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-surface-900">Your Destinations</h3>
                  <p className="text-xs text-surface-500 mt-0.5">
                    {destinations.length} destination{destinations.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={openAdd}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {error && (
                <div className="mb-3 px-3 py-2 rounded-lg banner-red border text-xs">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-8"><Spinner /></div>
              ) : destinations.length === 0 ? (
                /* Empty state */
                <div className="text-center py-10 border-2 border-dashed border-surface-300 rounded-xl">
                  <MapPin className="w-8 h-8 text-surface-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-surface-600">No destinations yet</p>
                  <p className="text-xs text-surface-400 mt-1">Add your first showcase destination to get started</p>
                  <button
                    onClick={openAdd}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Destination
                  </button>
                </div>
              ) : (
                /* Destinations list */
                <div className="space-y-2">
                  {destinations.map((dest, i) => (
                    <div
                      key={dest.id}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-xl border transition-all group',
                        dest.is_active
                          ? 'border-surface-300 bg-surface-200 hover:border-surface-400'
                          : 'border-surface-300 bg-surface-50 opacity-60'
                      )}
                    >
                      {/* Sort arrows */}
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          onClick={() => moveUp(i)}
                          disabled={i === 0}
                          className="p-0.5 rounded text-surface-400 hover:text-surface-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => moveDown(i)}
                          disabled={i >= destinations.length - 1}
                          className="p-0.5 rounded text-surface-400 hover:text-surface-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Image thumbnail */}
                      <div className="w-14 h-10 rounded-lg overflow-hidden bg-surface-100 flex-shrink-0">
                        {dest.image_url ? (
                          <img
                            src={dest.image_url}
                            alt={dest.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-4 h-4 text-surface-400" />
                          </div>
                        )}
                      </div>

                      {/* Name + subtitle */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-surface-900 truncate">{dest.name}</div>
                        {dest.subtitle && (
                          <div className="text-xs text-surface-500 truncate">{dest.subtitle}</div>
                        )}
                        {dest.tags.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {dest.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded-full bg-surface-100 text-surface-500">
                                {tag}
                              </span>
                            ))}
                            {dest.tags.length > 3 && (
                              <span className="text-[10px] text-surface-400">+{dest.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Price + Season badges */}
                      <div className="hidden sm:flex flex-col gap-1 text-right shrink-0">
                        {dest.price_range && (
                          <span className="text-[10px] text-surface-500 flex items-center gap-1 justify-end">
                            <DollarSign className="w-3 h-3" />{dest.price_range}
                          </span>
                        )}
                        {dest.best_season && (
                          <span className="text-[10px] text-surface-500 flex items-center gap-1 justify-end">
                            <Calendar className="w-3 h-3" />{dest.best_season}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => toggleActive(dest)}
                          className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-700"
                          title={dest.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {dest.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => openEdit(dest)}
                          className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-emerald-600 dark:text-emerald-400"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteId(dest.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-50 dark:bg-red-900/20 text-surface-400 hover:text-red-600 dark:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* ADD / EDIT MODAL                                               */}
      {/* ════════════════════════════════════════════════════════════════ */}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit Destination' : 'Add Destination'}
        subtitle="Fill in the details for this showcase destination"
        wide
      >
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              Name <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              className="input-field text-sm"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              placeholder="e.g. Bali"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">Subtitle</label>
            <input
              className="input-field text-sm"
              value={form.subtitle}
              onChange={e => setField('subtitle', e.target.value)}
              placeholder="e.g. Indonesia — Island of the Gods"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">Description</label>
            <textarea
              className="input-field text-sm resize-none"
              rows={3}
              value={form.description}
              onChange={e => setField('description', e.target.value)}
              placeholder="A longer description shown on rich card layouts..."
            />
          </div>

          {/* Image URL + preview */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">Image URL</label>
            <input
              className="input-field text-sm"
              value={form.image_url}
              onChange={e => setField('image_url', e.target.value)}
              placeholder="https://images.unsplash.com/photo-..."
            />
            {form.image_url && (
              <div className="mt-2 rounded-lg overflow-hidden border border-surface-200 max-w-xs">
                <img
                  src={form.image_url}
                  alt="Preview"
                  className="w-full h-32 object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          {/* Price Range + Best Season — side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                <DollarSign className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Price Range
              </label>
              <input
                className="input-field text-sm"
                value={form.price_range}
                onChange={e => setField('price_range', e.target.value)}
                placeholder="$500-$1200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">
                <Calendar className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Best Season
              </label>
              <input
                className="input-field text-sm"
                value={form.best_season}
                onChange={e => setField('best_season', e.target.value)}
                placeholder="Apr-Oct"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              <Tag className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Tags
            </label>
            <input
              className="input-field text-sm"
              value={form.tags}
              onChange={e => setField('tags', e.target.value)}
              placeholder="beach, culture, adventure (comma-separated)"
            />
            {form.tags && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {form.tags.split(',').map(t => t.trim()).filter(Boolean).map(tag => (
                  <span key={tag} className="px-2 py-0.5 text-xs rounded-full badge-emerald border border-emerald-800/40">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Link URL */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              <ExternalLink className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Link URL
            </label>
            <input
              className="input-field text-sm"
              value={form.link_url}
              onChange={e => setField('link_url', e.target.value)}
              placeholder="https://youragency.com/destinations/bali"
            />
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-surface-200">
            <button
              type="button"
              role="switch"
              aria-checked={form.is_active}
              onClick={() => setField('is_active', !form.is_active)}
              className={cn(
                'w-10 h-5 rounded-full transition-colors relative shrink-0',
                form.is_active ? 'bg-emerald-500' : 'bg-slate-400'
              )}
            >
              <div
                className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                style={{ transform: form.is_active ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
            <span className="text-sm text-surface-700">Active</span>
          </label>
        </div>

        {/* Modal actions */}
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-surface-200">
          <button
            onClick={() => setModalOpen(false)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving && <Spinner size="sm" />}
            {editingId ? 'Save Changes' : 'Add Destination'}
          </button>
        </div>
      </Modal>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* DELETE CONFIRMATION MODAL                                      */}
      {/* ════════════════════════════════════════════════════════════════ */}

      <Modal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete Destination"
        subtitle="This action cannot be undone"
      >
        <p className="text-sm text-surface-600 mb-5">
          Are you sure you want to delete{' '}
          <strong>{destinations.find(d => d.id === deleteId)?.name}</strong>?
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setDeleteId(null)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-surface-600 hover:bg-surface-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {deleting && <Spinner size="sm" />}
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
